import { Info } from '@mui/icons-material';
import { Box, BoxProps, Button, Tooltip } from '@mui/material';
import { FormEvent } from 'react';
import { Control, UseFormGetValues, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../../../common/auth/PermissionsContext';
import {
  ApiconPriceFormUiState,
  ApiconSpecialCasePriceFormState,
} from '../../../../../generated/ApiClient';
import { FinancesDrawerFormValues } from '../RightDrawerSchema';
import { ServiceVariant } from './FinancesServiceVariant';
import { FinancesSpecialCaseView } from './FinancesSpecialCaseView';
import { FinancesNormalPriceView } from './FinancesNormalPriceView';

type Props = {
  control: Control<FinancesDrawerFormValues>;
  getValues: UseFormGetValues<FinancesDrawerFormValues>;
  loading: boolean;
  readonly?: boolean;
  dealId?: number;
  calculationId?: number;
  serviceVariants: ServiceVariant[];
  serviceVariantId: number | undefined;
  onCalculateServicePrice: () => void;
  onSaveServiceVariant: (event: FormEvent, isServiceVariantSaveRequest: boolean) => void;
  onSpecialCasePriceButtonClicked: () => void;
  onCompleteSpecialCasePrice: () => void;
} & BoxProps;
export const FinancesServiceCalculation = ({
  control,
  loading,
  readonly,
  dealId,
  calculationId,
  serviceVariants,
  serviceVariantId,
  onCalculateServicePrice,
  onSaveServiceVariant,
  onSpecialCasePriceButtonClicked,
  onCompleteSpecialCasePrice,
  getValues,
  ...others
}: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canEditServices = hasPermission('Services', 'Edit') && !readonly;
  const isCalculationSaved = calculationId !== undefined && calculationId > 0;
  const isServiceVariantSelected = serviceVariantId !== undefined;

  const apiconSpecialCaseStateText = () => {
    if (specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestInProgress)
      return t(
        'deals.new.financesConfiguration.calculationDrawer.options.specialCasePrice.inProgress'
      );
    if (
      specialCaseRequestStatusReceived &&
      apiconPriceFormPriceFormState === ApiconSpecialCasePriceFormState.SpecialCaseRequestApproved
    )
      return t(
        'deals.new.financesConfiguration.calculationDrawer.options.specialCasePrice.approved'
      );
    if (
      specialCaseRequestStatusReceived &&
      apiconPriceFormPriceFormState === ApiconSpecialCasePriceFormState.SpecialCaseRequestRejected
    )
      return t(
        'deals.new.financesConfiguration.calculationDrawer.options.specialCasePrice.rejected'
      );
  };

  const apiconPriceFormPriceFormState = useWatch({
    control,
    name: 'service.apiconPriceForm.priceFormState',
  });

  const specialCasePriceFormUiState = useWatch({
    control,
    name: 'service.apiconSpecialCase.priceFormUiState',
  });

  const proposalCreationDate = useWatch({
    control,
    name: 'service.apiconProposalForm.proposalCreationDate',
  });

  const specialCasePriceFormState = useWatch({
    control,
    name: 'service.apiconSpecialCase.priceFormState',
  });

  const specialCaseRequestStatusReceived =
    (specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestApproved ||
      specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestRejected) &&
    (specialCasePriceFormState === ApiconSpecialCasePriceFormState.SpecialCaseRequestApproved ||
      specialCasePriceFormState === ApiconSpecialCasePriceFormState.SpecialCaseRequestRejected);

  const displaySpecialPriceCreateButtonAndShowPrice =
    specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestDisplayPrice ||
    specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestFinishedWithPrice;

  const displaySpecialCasePriceInitialView =
    apiconPriceFormPriceFormState === ApiconSpecialCasePriceFormState.SpecialCasePrice &&
    (specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestIsPriceOnRequest ||
      specialCasePriceFormUiState ===
        ApiconPriceFormUiState.SpecialCaseRequestFinishedIsPriceOnRequest);

  const showSpecial =
    specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestDisplayPrice ||
    specialCasePriceFormUiState === ApiconPriceFormUiState.SpecialCaseRequestFinishedWithPrice;

  return (
    <Box
      sx={{
        width: 1 / 2,
        p: 2,
        pr: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
      {...others}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {displaySpecialCasePriceInitialView ? (
          <Box>{t('deals.new.financesConfiguration.calculationDrawer.options.priceOnRequest')}</Box>
        ) : showSpecial ? (
          <FinancesSpecialCaseView key="special" control={control} />
        ) : (
          <FinancesNormalPriceView key="normal" control={control} />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '150px',
          justifyContent: 'flex-end',
        }}
      >
        {canEditServices && (
          <Button
            onClick={onCalculateServicePrice}
            disabled={!canEditServices || loading || proposalCreationDate != null}
            variant="contained"
            type="button"
            sx={{
              height: '28px',
              width: '100px',
              alignSelf: 'flex-end',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
            }}
          >
            {t('deals.new.financesConfiguration.calculationDrawer.options.priceInquiry')}
          </Button>
        )}
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: '2%' }}>
          {dealId === undefined && (
            <Tooltip
              placement="right"
              title={t('deals.new.financesConfiguration.calculationDrawer.options.saveVariantHint')}
            >
              <Info color="primary" />
            </Tooltip>
          )}
          <Button
            onClick={(event) => onSaveServiceVariant(event, true)} // TODO Valierungen wie im alten
            disabled={
              !canEditServices || loading || dealId === undefined || proposalCreationDate != null
            }
            sx={{
              height: '28px',
              width: '169px',
              border: 1,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
            }}
          >
            {t('deals.new.financesConfiguration.calculationDrawer.options.saveAsVariant')}
          </Button>
          {apiconPriceFormPriceFormState === ApiconSpecialCasePriceFormState.SpecialCasePrice && (
            <Box>
              <Box>{apiconSpecialCaseStateText()}</Box>
              {(displaySpecialPriceCreateButtonAndShowPrice ||
                displaySpecialCasePriceInitialView) && (
                <Button
                  disabled={
                    !canEditServices ||
                    loading ||
                    !isCalculationSaved ||
                    !isServiceVariantSelected ||
                    proposalCreationDate != null ||
                    serviceVariants.length < 1
                  }
                  onClick={onSpecialCasePriceButtonClicked}
                  sx={{
                    height: '28px',
                    width: '269px',
                    border: 1,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                  }}
                >
                  {t(
                    'deals.new.financesConfiguration.calculationDrawer.options.specialPriceRequest'
                  )}
                </Button>
              )}
              {specialCaseRequestStatusReceived && (
                <Button
                  disabled={loading || !isCalculationSaved || !isServiceVariantSelected}
                  onClick={onCompleteSpecialCasePrice}
                  sx={{
                    height: '28px',
                    width: '269px',
                    border: 1,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                  }}
                >
                  {t(
                    'deals.new.financesConfiguration.calculationDrawer.options.completeSpecialPriceRequest'
                  )}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
